import React from 'react';
import { motion } from 'framer-motion';
import { BookOpen } from 'lucide-react';
import SiloNav from './SiloNav';
import type { SiloId } from '../../types/blog';

interface BlogHeroProps {
  title?: string;
  subtitle?: string;
  activeSilo?: SiloId;
}

const BlogHero: React.FC<BlogHeroProps> = ({
  title = 'Blog Dépannage Informatique',
  subtitle = 'Guides pratiques, astuces et solutions pour tous vos problèmes informatiques — rédigés par nos techniciens.',
  activeSilo,
}) => {
  const revealUp = {
    hidden: { opacity: 0, y: 24 },
    show: { opacity: 1, y: 0 },
  };

  return (
    <div className="relative py-20 min-h-[320px] overflow-hidden bg-[#2F3136]">
      <div className="absolute inset-0">
        <img
          src="/images/heroes/blog-hero.webp"
          alt="Illustration du blog SOS IT Services"
          className="h-full w-full object-cover opacity-90"
          loading="eager"
        />
      </div>
      <div className="pointer-events-none absolute inset-0 bg-[#2F3136]/40" />
      <div className="pointer-events-none absolute inset-0">
        <motion.div
          className="absolute -top-20 left-[8%] h-64 w-64 rounded-full bg-[#5865F2]/20 blur-3xl"
          animate={{ x: [0, 18, -8, 0], y: [0, -12, 14, 0] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute -bottom-20 right-[6%] h-80 w-80 rounded-full bg-cyan-400/10 blur-3xl"
          animate={{ x: [0, -22, 10, 0], y: [0, 12, -18, 0] }}
          transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          variants={revealUp}
          initial="hidden"
          animate="show"
          transition={{ duration: 0.55, ease: 'easeOut' }}
        >
          <motion.span
            className="inline-flex items-center gap-2 rounded-full border border-[#5865F2]/40 bg-[#5865F2]/15 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-[#B9C4FF] mb-5"
            animate={{
              boxShadow: [
                '0 0 0 rgba(88,101,242,0)',
                '0 0 24px rgba(88,101,242,0.25)',
                '0 0 0 rgba(88,101,242,0)',
              ],
            }}
            transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}
          >
            <BookOpen className="h-3.5 w-3.5" />
            Guides & Conseils
          </motion.span>

          <h1 className="text-3xl md:text-5xl font-bold text-white mb-5">
            {title.includes('Dépannage') ? (
              <>
                Blog{' '}
                <span className="text-[#5865F2]">Dépannage Informatique</span>
              </>
            ) : (
              title
            )}
          </h1>

          <p className="text-gray-300 text-base md:text-lg max-w-2xl mx-auto">{subtitle}</p>
        </motion.div>

        <motion.div
          className="mt-8"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.08, ease: 'easeOut' }}
        >
          <SiloNav activeSilo={activeSilo} />
        </motion.div>
      </div>
    </div>
  );
};

export default BlogHero;
